import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message);
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email address.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#6F503A] text-[#F8F5EF]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-12">
          <div className="space-y-5">
            <Logo className="h-8" light />
            <p className="text-[#F8F5EF]/70 text-sm max-w-xs leading-relaxed">
              Gentle, fragrance-free skincare support for babies and children with sensitive, eczema-prone skin.
            </p>
            <form onSubmit={subscribe} className="flex gap-2 max-w-sm" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="newsletter-input"
                className="flex-1 bg-[#F8F5EF]/10 border border-[#F8F5EF]/20 rounded-full px-5 py-3 text-sm text-[#F8F5EF] placeholder:text-[#F8F5EF]/50 focus:outline-none focus:ring-2 focus:ring-[#B8C4A3]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="px-5 rounded-full bg-[#B8C4A3] text-[#6F503A] font-medium hover:bg-[#a7b48f] transition-colors flex items-center"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </div>

          <FooterCol
            title="Shop"
            links={[
              { label: "Eczema Patches", to: "/product/patch" },
              { label: "Calm Balm Stick", to: "/product/balm" },
              { label: "Patch + Balm Bundle", to: "/product/bundle" },
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              { label: "About", to: "/" },
              { label: "Reviews", to: "/" },
              { label: "Contact", to: "/" },
            ]}
          />
          <FooterCol
            title="Policies"
            links={[
              { label: "Shipping", to: "/" },
              { label: "Returns", to: "/" },
              { label: "Privacy", to: "/" },
            ]}
          />
        </div>

        <div className="mt-14 pt-8 border-t border-[#F8F5EF]/15 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#F8F5EF]/60">© {new Date().getFullYear()} Baby Hemp. All rights reserved.</p>
          <div className="flex items-center gap-3">
            {[Instagram, Facebook, Twitter].map((Icon, idx) => (
              <a key={idx} href="/" className="p-2.5 rounded-full bg-[#F8F5EF]/10 hover:bg-[#F8F5EF]/20 transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="font-heading font-semibold mb-4">{title}</h4>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.label}>
          <Link to={l.to} className="text-sm text-[#F8F5EF]/70 hover:text-[#F8F5EF] transition-colors">
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);
